import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Cpu, Languages, Landmark, ClipboardCheck, ScanSearch, Plus } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const ICONS = [Landmark, Users, ClipboardCheck, Cpu, Languages, ScanSearch];
const CLIP_IDS = [1, 2, 5, 3, 4, 6];
const CLIPS = CLIP_IDS.map((n) => ({ webm: `/videos/usp-${n}.webm`, mp4: `/videos/usp-${n}.mp4` }));

export const Usp = ({ t }) => {
  const [open, setOpen] = useState(null);

  return (
    <section id="staerken" data-testid="usp-section" className="relative py-24 sm:py-32 px-4">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow>{t.usp.label}</Eyebrow>
          <h2 className="mt-4 font-jakarta text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            {t.usp.title}
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">{t.usp.sub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {t.usp.cards.map((card, i) => {
            const Icon = ICONS[i % ICONS.length];
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={i * 0.1}>
                <div
                  data-testid={`usp-card-${i}`}
                  className={`group relative h-full p-8 rounded-2xl bg-surface border transition-all duration-300 shadow-sm hover:shadow-xl ${
                    isOpen ? "border-[#003940]/50 shadow-xl" : "border-slate-200 hover:border-[#003940]/50"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    data-testid={`usp-card-toggle-${i}`}
                    className="w-full text-left cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className={`w-12 h-12 rounded-xl border flex items-center justify-center transition-all duration-300 ${
                          isOpen
                            ? "bg-[#003940] text-white border-[#003940]"
                            : "bg-[#003940]/8 border-[#003940]/20 text-[#003940] group-hover:bg-[#003940] group-hover:text-white"
                        }`}
                      >
                        <Icon size={22} />
                      </div>
                      <span
                        className={`mt-1 w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${
                          isOpen
                            ? "bg-[#003940] border-[#003940] text-white rotate-45"
                            : "border-slate-300 text-slate-500 group-hover:border-[#003940]/50 group-hover:text-[#003940]"
                        }`}
                      >
                        <Plus size={15} />
                      </span>
                    </div>
                    <h3 className="mt-6 text-xl font-semibold tracking-tight text-slate-900">{card.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{card.text}</p>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-[#0A272C]">
                          <video
                            data-testid={`usp-card-video-${i}`}
                            className="w-full aspect-video object-cover"
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            aria-label={card.title}
                          >
                            <source src={CLIPS[i].webm} type="video/webm" />
                            <source src={CLIPS[i].mp4} type="video/mp4" />
                          </video>
                        </div>
                        <p
                          data-testid={`usp-card-detail-${i}`}
                          className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600 leading-relaxed"
                        >
                          {card.more}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
